<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import {
    OnboardingPackagesService,
    FormsService,
    QuestionnairesService,
    UnifiedDocumentTemplatesService,
    type CustomerAdminOnboardingPackage as OnboardingPackage,
    type CustomerQuestionnaire,
    type CustomerForm,
    type UnifiedDocumentTemplate,
    FlagEnum
  } from '@/types/generated'
  import { onMounted, ref, computed, watch } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { Button } from '@/components/ui/button'
  import * as yup from 'yup'
  import { useRouter } from 'vue-router'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import DocumentTemplateSection from '@/components/app/email/documentTemplateSection.vue'
  import QuestionnaireTemplateSection from '@/components/app/email/questionnaireTemplateSection.vue'
  import { genericErrorToast, saveSuccessToast } from '@/lib/toasts'
  import { debounce } from '@/lib/timing'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import { storeToRefs } from 'pinia'
  import { ROUTES } from '@/router/routes'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import { SlashIcon, EyeIcon } from '@heroicons/vue/24/outline'

  const { t } = useTranslation({
    en: {
      clientOnboarding: 'Client Onboarding',
      newOnboardingPackage: 'New Onboarding Package',
      preview: 'Preview',
      name: 'Name',
      namePlaceholder: 'Select law field and law topic to fill out the name automatically',
      lawField: 'Law field',
      lawTopic: 'Law topic',
      notSelected: 'Not selected',
      onboardingPackage: 'Onboarding package'
    },
    de: {
      clientOnboarding: 'Mandanten-Onboarding',
      newOnboardingPackage: 'Neues Onboarding-Paket',
      preview: 'Vorschau',
      name: 'Name',
      namePlaceholder:
        'Wählen Sie Rechtsgebiet und Rechtsthema, um den Namen automatisch auszufüllen',
      lawField: 'Rechtsgebiet',
      lawTopic: 'Rechtsthema',
      notSelected: 'Nicht ausgewählt',
      onboardingPackage: 'Onboarding-Paket'
    }
  })

  const router = useRouter()
  const onboardingPackage = ref<OnboardingPackage>()
  const pdfFormTemplatesEnabled = useFeatureFlag(FlagEnum.PDF_FORM_TEMPLATES)
  const allForms = ref<CustomerForm[]>([])
  const allUnifiedDocuments = ref<UnifiedDocumentTemplate[]>([])
  const allQuestionnaires = ref<CustomerQuestionnaire[]>([])
  const isLoading = ref(true)
  const userStore = useUserStore()
  const { user } = storeToRefs(userStore)
  const { fetchUser } = userStore
  const { fetchLawFields } = useLawFieldStore()

  const isNewPackage = computed(() => router.currentRoute.value.params.uuid === 'new')

  // Create local computed for lawFieldOptions since the store might not have it
  const lawFieldOptions = computed(() => {
    const lawFields = user.value?.org.law_fields || []
    // If law_fields is an object, get the keys; if array, use directly
    const fieldList = Array.isArray(lawFields) ? lawFields : Object.keys(lawFields)
    return fieldList.map((lawField) => ({
      value: lawField,
      title: lawField
    }))
  })

  const lawFields = computed(() => useLawFieldStore().lawFields)

  const lawTopicOptions = computed(() => {
    if (!onboardingPackage.value?.law_field) return []
    if (!lawFields.value?.data) return []

    const topics = lawFields.value.data[onboardingPackage.value.law_field] || []

    return topics.map((topic: string) => ({
      value: topic,
      title: topic
    }))
  })

  // Auto-update name based on law field and topic
  watch(
    [() => onboardingPackage.value?.law_field, () => onboardingPackage.value?.law_topic],
    ([lawField, lawTopic]) => {
      if (!onboardingPackage.value) return

      if (lawField && lawTopic) {
        onboardingPackage.value.name = `${lawField}/${lawTopic} ${t('onboardingPackage')}`
      } else if (lawField) {
        onboardingPackage.value.name = `${lawField} ${t('onboardingPackage')}`
      }
    }
  )

  const formsPage = ref(1)
  const formsPerPage = ref(10)
  const totalForms = ref(0)
  const selectedFormIds = ref<string[]>([])

  const questionnairesPage = ref(1)
  const questionnairesPerPage = ref(10)
  const totalQuestionnaires = ref(0)
  const selectedQuestionnaireUuids = ref<string[]>([])

  onMounted(async () => {
    isLoading.value = true

    await pdfFormTemplatesEnabled.checkFlag()

    try {
      const fetching = [
        isNewPackage.value ? initializeNewPackage() : fetchPackage(),
        pdfFormTemplatesEnabled.isEnabled.value ? fetchAllUnifiedDocuments() : fetchAllForms(),
        fetchAllQuestionnaires(),
        fetchUser(),
        fetchLawFields()
      ]
      await Promise.all(fetching)
    } finally {
      isLoading.value = false
    }
  })

  const initializeNewPackage = async () => {
    onboardingPackage.value = {
      uuid: '',
      name: '',
      questionnaires: [],
      forms: [],
      pdf_form_templates: [],
      law_field: undefined,
      law_topic: undefined,
      updated_at: new Date().toISOString()
    } as OnboardingPackage

    selectedFormIds.value = []
    selectedQuestionnaireUuids.value = []
  }

  const fetchPackage = async () => {
    onboardingPackage.value = (await OnboardingPackagesService.onboardingPackagesRetrieve({
      uuid: router.currentRoute.value.params.uuid.toString()
    })) as OnboardingPackage

    // Combine legacy forms and PDF form templates IDs
    const legacyFormIds = onboardingPackage.value?.forms.map((form) => form.id) || []
    const pdfFormTemplateIds =
      (onboardingPackage.value as any)?.pdf_form_templates?.map((pdfForm: any) => pdfForm.uuid) ||
      []
    selectedFormIds.value = [...legacyFormIds, ...pdfFormTemplateIds]

    selectedQuestionnaireUuids.value =
      onboardingPackage.value?.questionnaires.map((q) => q.uuid) || []
  }

  const fetchAllForms = async (searchQuery?: string) => {
    const response = await FormsService.formsList({
      tag: 'MASTER',
      limit: formsPerPage.value,
      offset: (formsPage.value - 1) * formsPerPage.value,
      search: searchQuery
    })
    allForms.value = response.results as CustomerForm[]
    totalForms.value = response.count
  }

  const fetchAllUnifiedDocuments = async (searchQuery?: string) => {
    const response = await UnifiedDocumentTemplatesService.unifiedDocumentTemplatesList({
      limit: formsPerPage.value,
      offset: (formsPage.value - 1) * formsPerPage.value,
      search: searchQuery
    })
    allUnifiedDocuments.value = response.results || []
    totalForms.value = response.count || 0
  }

  const fetchAllQuestionnaires = async (searchQuery?: string) => {
    const response = await QuestionnairesService.questionnairesList({
      limit: questionnairesPerPage.value,
      offset: (questionnairesPage.value - 1) * questionnairesPerPage.value,
      search: searchQuery
    })
    allQuestionnaires.value = response.results as CustomerQuestionnaire[]
    totalQuestionnaires.value = response.count
  }

  const handleFormsPageChange = async (page: number) => {
    formsPage.value = page
    if (pdfFormTemplatesEnabled.isEnabled.value) {
      await fetchAllUnifiedDocuments()
    } else {
      await fetchAllForms()
    }
  }

  const handleFormsSearch = async (query: string) => {
    formsPage.value = 1
    if (pdfFormTemplatesEnabled.isEnabled.value) {
      await fetchAllUnifiedDocuments(query)
    } else {
      await fetchAllForms(query)
    }
  }

  const handleQuestionnairesPageChange = async (page: number) => {
    questionnairesPage.value = page
    await fetchAllQuestionnaires()
  }

  const handleQuestionnairesSearch = async (query: string) => {
    questionnairesPage.value = 1
    await fetchAllQuestionnaires(query)
  }

  const handleSelectedFormsUpdate = (newSelectedFormIds: string[]) => {
    selectedFormIds.value = newSelectedFormIds
  }

  const handleSelectedQuestionnairesUpdate = (newSelectedQuestionnaireIds: string[]) => {
    selectedQuestionnaireUuids.value = newSelectedQuestionnaireIds
  }

  const handleFormSave = async (values: any) => {
    if (!onboardingPackage.value) return

    try {
      if (isNewPackage.value) {
        // Create new package
        const created = await OnboardingPackagesService.onboardingPackagesCreate({
          requestBody: {
            name: values.name,
            law_field: values.law_field,
            law_topic: values.law_topic,
            form_uuids: selectedFormIds.value,
            questionnaire_uuids: selectedQuestionnaireUuids.value
          }
        })

        // Navigate to the newly created package
        router.replace(
          ROUTES.APP.SETTINGS.CLIENT_ONBOARDING.ONBOARDING_PACKAGE_DETAIL.replace(
            ':uuid',
            (created as any).uuid
          )
        )
        saveSuccessToast()
      } else {
        // Update existing package
        await OnboardingPackagesService.onboardingPackagesPartialUpdate({
          uuid: onboardingPackage.value.uuid,
          requestBody: {
            ...values,
            form_uuids: selectedFormIds.value,
            questionnaire_uuids: selectedQuestionnaireUuids.value
          }
        })
      }
    } catch (error) {
      console.error('Error saving onboarding package:', error)
      genericErrorToast()
    }
  }

  const savePackageSelections = async () => {
    if (!onboardingPackage.value || isNewPackage.value) return

    try {
      await OnboardingPackagesService.onboardingPackagesPartialUpdate({
        uuid: onboardingPackage.value.uuid,
        requestBody: {
          name: onboardingPackage.value.name,
          law_field: onboardingPackage.value.law_field,
          law_topic: onboardingPackage.value.law_topic,
          form_uuids: selectedFormIds.value,
          questionnaire_uuids: selectedQuestionnaireUuids.value
        }
      })
    } catch (error) {
      console.error('Error saving selections:', error)
    }
  }

  const debouncedSaveSelections = debounce(savePackageSelections, 1000)

  // Watch for changes in sections
  watch([selectedFormIds, selectedQuestionnaireUuids], () => {
    if (!isNewPackage.value) {
      debouncedSaveSelections()
    }
  })
</script>

<template>
  <div
    v-if="onboardingPackage && user"
    class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
  >
    <!-- Card content -->
    <SettingsPage>
      <template #breadcrumbs>
        <ol class="flex items-center whitespace-nowrap">
          <li class="inline-flex items-center">
            <span class="flex items-center text-sm text-gray-500 focus:outline-none">
              {{ t('clientOnboarding') }}
            </span>
            <SlashIcon class="size-5 shrink-0 text-gray-400" />
          </li>
          <li class="inline-flex items-center truncate text-sm font-semibold text-gray-800">
            {{ onboardingPackage.name || t('newOnboardingPackage') }}
          </li>
        </ol>
      </template>
      <template #actions>
        <div class="flex items-center space-x-2">
          <button
            class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
              font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
              disabled:pointer-events-none disabled:opacity-50"
          >
            <EyeIcon class="h-5 w-5" />
            {{ t('preview') }}
          </button>
        </div>
      </template>
      <AutoSavingForm @save="handleFormSave">
        <div class="mb-4">
          <InputField
            v-model="onboardingPackage.name"
            name="name"
            :label="t('name')"
            :rules="yup.string().required()"
            :placeholder="t('namePlaceholder')"
          />
          <!-- <p class="mt-1 text-sm text-gray-500">
            Select law field and law topic to fill out the name automatically
          </p> -->
          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <SelectField
              v-model="onboardingPackage.law_field"
              :options="lawFieldOptions"
              name="law_field"
              :title="t('lawField')"
              :label="t('lawField')"
              :allow-unselect="true"
              :unselected-option-text="t('notSelected')"
            />
            <SelectField
              v-model="onboardingPackage.law_topic"
              :options="lawTopicOptions"
              name="law_topic"
              :title="t('lawTopic')"
              :label="t('lawTopic')"
              :allow-unselect="true"
              :unselected-option-text="t('notSelected')"
              :disabled="!onboardingPackage.law_field"
            />
          </div>
        </div>
      </AutoSavingForm>
      <div v-if="!isLoading">
        <QuestionnaireTemplateSection
          :questionnaires="allQuestionnaires"
          :selected-questionnaire-uuids="selectedQuestionnaireUuids"
          :current-page="questionnairesPage"
          :total-items="totalQuestionnaires"
          :items-per-page="questionnairesPerPage"
          @update:selected-questionnaires="handleSelectedQuestionnairesUpdate"
          @page-change="handleQuestionnairesPageChange"
          @search-change="handleQuestionnairesSearch"
        />
        <DocumentTemplateSection
          v-if="!pdfFormTemplatesEnabled.isEnabled.value"
          :forms="allForms"
          :selected-form-ids="selectedFormIds"
          :current-page="formsPage"
          :total-items="totalForms"
          :items-per-page="formsPerPage"
          @page-change="handleFormsPageChange"
          @update:selected-documents="handleSelectedFormsUpdate"
          @search-change="handleFormsSearch"
        />
        <DocumentTemplateSection
          v-else
          :forms="allUnifiedDocuments"
          :selected-form-ids="selectedFormIds"
          :current-page="formsPage"
          :total-items="totalForms"
          :items-per-page="formsPerPage"
          @page-change="handleFormsPageChange"
          @update:selected-documents="handleSelectedFormsUpdate"
          @search-change="handleFormsSearch"
        />
      </div>
      <div
        v-else
        class="flex justify-center py-8"
      >
        <div
          class="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"
        ></div>
      </div>
    </SettingsPage>
  </div>
</template>
